"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  solvePlate,
  PLATE_FLOOR,
  PLATE_LH,
  MAX_LINES,
  PLATE_MOBILE_CAP,
  PLATE_DESKTOP_CAP,
  PLATE_MD_BREAKPOINT,
  plateFallbackFor,
  plateHeightBudgetFor,
  type PlateFit,
} from "@/lib/plate-and-hairline";

/**
 * The plate, with the measure-and-step pass from §08.2.
 *
 * The server pass solves the string against the measured font (advances and
 * kerning, lib/plate-and-hairline-metrics.ts) at BOTH breakpoints, and the
 * markup carries both answers: the mobile one and the desktop one, as custom
 * properties a breakpoint rule selects before any script runs. So the first
 * paint is already the size the client will settle on — for the container
 * width the server was told about. A container the server did not know
 * (a viewport between the two references, a sidebar that changes the column)
 * still re-solves on hydration.
 *
 * The client then measures the line boxes the browser actually produced and
 * bisects only if they overran. With a solver that reads the same font table
 * the browser does, that is now the exceptional path rather than the usual
 * one; before it, the server modelled an average advance width and the
 * client overruled it on eight of ten landing pages, moving everything under
 * the heading by 150–210px after hydration (CLS 0.05–0.08, 2026-09-03 review).
 *
 * Measurement reads the rendered size out of the DOM rather than out of state.
 * That is both more truthful (it measures what the browser actually did) and
 * keeps `size` out of the callback's dependencies, so the ResizeObserver
 * subscription stays stable. It never measures a fallback face: until the
 * plate's font reports loaded, the bracket is left alone, because a bisection
 * against the wrong metrics commits the wrong size (one load in sixteen of
 * /sounds/aww settled at 83px instead of 151px).
 */

/**
 * How many probes the search may take before it commits.
 *
 * Each probe costs a layout, so this is a real budget rather than a formality.
 * Four halvings narrow the 56..184 range to about 8px, which is finer than the
 * type scale distinguishes, and the search usually converges before spending
 * them. It commits the best size found either way, so exhausting the budget
 * degrades the answer slightly rather than breaking it.
 */
const MAX_STEPS = 4;

/** useLayoutEffect warns during SSR; standard isomorphic shim. */
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Server-pass container widths, in px, when the caller gives none.
 *
 * Mobile: a 390px viewport minus the 24px gutter each side. Desktop: the
 * heading column measured at 1440 on the landing, install and sound-detail
 * routes (976px); pages with a different column (the wide library header,
 * the model guides' prose measure, /download's 64rem cap) pass their own
 * `usableDesktop`. The client re-solves from the real container either way,
 * so a wrong hint costs a re-solve, never a wrong plate.
 */
export const DEFAULT_USABLE_MOBILE = 342;
export const DEFAULT_USABLE_DESKTOP = 976;

/**
 * The breakpoint rule that picks the server's mobile or desktop answer.
 *
 * Inline styles cannot carry a media query, and the server cannot know the
 * viewport, so the wrapper carries both answers as custom properties and
 * this rule maps the active pair onto the properties the type reads. It is a
 * hoisted <style> (React dedupes it by `href`) rather than a line in
 * app/plate-and-hairline.css so the component is correct wherever it is
 * rendered, with no sheet to forget. Once the client has measured, the
 * wrapper's inline declarations override every property this sets.
 */
const BREAKPOINT_STYLE_HREF = "ls-plate-breakpoints";
const BREAKPOINT_STYLE = [
  ".ls-plate-type{",
  "--ls-plate-size:var(--ls-plate-sm-size);",
  "--ls-plate-weight:var(--ls-plate-sm-weight);",
  "--ls-plate-lh:var(--ls-plate-sm-lh);",
  "--ls-plate-track:var(--ls-plate-sm-track);",
  "--ls-plate-case:var(--ls-plate-sm-case);",
  "--ls-plate-shift:var(--ls-plate-sm-shift)}",
  `@media (min-width:${PLATE_MD_BREAKPOINT}px){.ls-plate-type{`,
  "--ls-plate-size:var(--ls-plate-md-size);",
  "--ls-plate-weight:var(--ls-plate-md-weight);",
  "--ls-plate-lh:var(--ls-plate-md-lh);",
  "--ls-plate-track:var(--ls-plate-md-track);",
  "--ls-plate-case:var(--ls-plate-md-case);",
  "--ls-plate-shift:var(--ls-plate-md-shift)}}",
].join("");

export interface PlateMeasurement {
  size: number;
  lines: number;
  /** Measured height of the rendered plate in px. */
  height: number;
  stepped: boolean;
  isPlate: boolean;
}

/** Plate cap for a viewport width. §01.4. */
function capFor(viewportWidth: number): number {
  return viewportWidth >= PLATE_MD_BREAKPOINT ? PLATE_DESKTOP_CAP : PLATE_MOBILE_CAP;
}

/** The treatment a fit implies. One place, so the two style modes agree. */
function treatmentFor(isPlate: boolean) {
  return {
    weight: isPlate ? 900 : 800,
    lineHeight: isPlate ? PLATE_LH : 1.02,
    letterSpacing: isPlate ? "-0.05em" : "-0.02em",
    textTransform: isPlate ? "uppercase" : "none",
    // §17.11 case 3 — the plate's own left edge. Side bearings scale with
    // type size, so at plate scale (56..184px) the glyph's built-in left
    // bearing is real pixels of empty space, and a geometrically-aligned
    // plate reads as indented against every hairline and label above it.
    // Only on a real plate: the sub-floor fallback renders as an ordinary
    // heading at h1-adjacent sizes, where side bearings are the few px this
    // system has never corrected for anywhere else.
    shift: isPlate ? "-0.06em" : "0px",
  } as const;
}

/**
 * Whether the face the plate is painting with is the loaded webfont.
 *
 * `document.fonts.check` answers for the font-family the element computed
 * to: false while Archivo is still unloaded or loading (the fallback face is
 * on screen), true once it is — and true, by the spec, when no @font-face
 * matches at all, which is the right answer for a page that never declared
 * one. Environments without the Font Loading API (jsdom) measure as before.
 */
function plateFontLoaded(el: HTMLElement): boolean {
  if (typeof document === "undefined" || !document.fonts?.check) return true;
  const spec = plateFontSpec(el);
  if (!spec) return true;
  try {
    // With the text, so a subset face (latin-ext for "Ü", say) counts too.
    return document.fonts.check(spec, el.textContent ?? "");
  } catch {
    return true;
  }
}

/**
 * The font the plate paints with, as the Font Loading API names one:
 * weight, size and the FIRST family of the stack, which is the webfont.
 * Empty when the element has no computed family to speak of.
 */
function plateFontSpec(el: HTMLElement): string {
  const cs = getComputedStyle(el);
  const family = cs.fontFamily.split(",")[0]?.trim();
  return family ? `${cs.fontWeight} ${cs.fontSize} ${family}` : "";
}

export interface PlateProps {
  /** The display portion only. Split the parenthetical off before this. */
  text: string;
  /**
   * Content width at the mobile reference (390px viewport), for the server
   * pass. Decides what renders below the md breakpoint before hydration.
   */
  usable?: number;
  /**
   * Content width at the desktop reference (1440px viewport), for the server
   * pass. Decides what renders at or above the md breakpoint before
   * hydration; the client re-solves from the real container either way.
   */
  usableDesktop?: number;
  /**
   * @deprecated The server pass solves both breakpoints now, so a cap no
   * longer chooses one. Kept so existing call sites compile: a caller that
   * passed `cap={184}` with `usable` meant the DESKTOP width, and is read
   * that way. Prefer `usable` + `usableDesktop`.
   */
  cap?: number;
  className?: string;
  /**
   * Element to render. Defaults to a div. Use "span" when the plate sits
   * inside a heading, so the markup stays valid and the heading keeps its own
   * text content: a div inside an h1 is invalid, and moving the text out of
   * the h1 would change what search engines read.
   */
  as?: "div" | "span";
  /** Reports what actually rendered. Used by the dev harness. */
  onMeasure?: (result: PlateMeasurement) => void;
  /**
   * Live signal level, 0 to 1, for rule 3. `undefined` keeps the stable signal
   * layer inactive, which is different from 0: both are visually empty, but
   * the latter represents a loaded, silent sound while the former means no
   * active audio session.
   */
  level?: number;
}

/** What the client has committed, or null while the server's pair stands. */
type ClientFit = { size: number; isPlate: boolean } | null;

export function Plate({
  text,
  usable,
  usableDesktop,
  cap,
  className,
  as = "div",
  onMeasure,
  level,
}: PlateProps) {
  // Where the band's top edge sits, as a percentage of the type's own height.
  // Clamped rather than trusted: a peak array can contain values above 1, and
  // an unclamped level would paint the whole glyph and read as a colour change
  // rather than a meter.
  const bandTop = level === undefined ? 0 : Math.round(Math.min(1, Math.max(0, level)) * 100);

  // The legacy `cap={184} usable={w}` shape described the desktop column.
  const legacyDesktop = cap !== undefined && cap >= PLATE_DESKTOP_CAP;
  const mobileUsable = legacyDesktop ? DEFAULT_USABLE_MOBILE : (usable ?? DEFAULT_USABLE_MOBILE);
  const desktopUsable = usableDesktop ?? (legacyDesktop ? usable : undefined) ?? DEFAULT_USABLE_DESKTOP;

  // The server pass: one answer per breakpoint, both from the measured font.
  const solvedSm: PlateFit = solvePlate({ chars: text.length, usable: mobileUsable, cap: PLATE_MOBILE_CAP, text });
  const solvedMd: PlateFit = solvePlate({ chars: text.length, usable: desktopUsable, cap: PLATE_DESKTOP_CAP, text });

  const spanRef = useRef<HTMLSpanElement>(null);
  const stepsRef = useRef(0);

  // The search bracket. `lo` is the largest size observed to fit, `hi` the
  // smallest observed to overrun, `best` the answer to commit. `hi` stays null
  // until something actually overruns, which is how the common case skips the
  // search entirely.
  const loRef = useRef(PLATE_FLOOR);
  const hiRef = useRef<number | null>(null);
  const bestRef = useRef<number | null>(null);

  const resetSearch = useCallback(() => {
    stepsRef.current = 0;
    loRef.current = PLATE_FLOOR;
    hiRef.current = null;
    bestRef.current = null;
  }, []);

  // Held in a ref so an inline callback from the parent does not invalidate
  // `measure` and re-subscribe the observer on every render.
  const onMeasureRef = useRef(onMeasure);
  useEffect(() => {
    onMeasureRef.current = onMeasure;
  }, [onMeasure]);

  // Null until the client has re-solved from the real container: the server's
  // per-breakpoint pair renders until then, and that is what hydration must
  // match. Starts null on server and client alike.
  const [fit, setFit] = useState<ClientFit>(null);

  // Bumped when the webfont loads, and included in the measure effect's deps.
  // Without it, fonts.ready's re-solve is a no-op: solvePlate is pure
  // arithmetic, so it returns the same answer it did before the font arrived,
  // setFit bails on equality, no render happens, and measure() never re-runs —
  // against a freshly cleared bracket. State (not a ref) deliberately: the
  // whole point is to force a render so the layout effect below re-measures
  // the real face.
  const [fontsNonce, setFontsNonce] = useState(0);

  // Whether measure() has already asked for the plate's face. One request per
  // mount; the promise settling is what re-runs the measurement.
  const fontRequestedRef = useRef(false);
  // Set once the request settles, loaded or not. A face that never arrives
  // (offline, blocked) must not leave the bracket frozen forever: after the
  // request settles the measurement proceeds against whatever is painting.
  const fontSettledRef = useRef(false);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The sweep must distinguish the server sizing hint from the client-measured
  // result. This marker is intentionally non-visual; writing it to the already
  // mounted wrapper avoids a state-only follow-up render.
  useIsomorphicLayoutEffect(() => {
    spanRef.current?.parentElement?.setAttribute("data-plate-hydrated", "true");
  }, []);

  // A new string or a new server answer is a fresh problem: back to the
  // server's pair and spend the step budget again. resolve() below runs in
  // the same commit and replaces the pair before paint.
  useIsomorphicLayoutEffect(() => {
    resetSearch();
    setFit(null);
  }, [solvedSm.size, solvedSm.isPlate, solvedMd.size, solvedMd.isPlate, text, resetSearch]);

  /**
   * Pass one, client side: re-solve from the container the plate is actually
   * in. The server guessed the column; this knows. Stepping down alone could
   * never recover the larger size when the viewport grows.
   */
  const resolve = useCallback(() => {
    const el = spanRef.current;
    if (!el) return;
    const box = el.parentElement?.getBoundingClientRect().width;
    if (!box) return;

    const nextCap = capFor(window.innerWidth);
    const next = solvePlate({ chars: text.length, usable: box, cap: nextCap, text });

    resetSearch();
    setFit((curr) =>
      curr && curr.size === next.size && curr.isPlate === next.isPlate
        ? curr
        : { size: next.size, isPlate: next.isPlate },
    );
  }, [text, resetSearch]);

  // The committed fit as the measure callback sees it. A ref, and written in
  // a layout effect declared BEFORE the measure effect, so measure() can tell
  // the server's pair from a client answer without `fit` in its deps (which
  // would re-subscribe the observer on every step).
  const fitRef = useRef<ClientFit>(null);
  useIsomorphicLayoutEffect(() => {
    fitRef.current = fit;
  });

  const measure = useCallback(() => {
    const el = spanRef.current;
    if (!el) return;

    // Only ever measure what the client committed. On mount this runs in the
    // same layout pass as resolve(), before resolve's answer has reached the
    // DOM, so what is on screen is still the server's per-breakpoint pair —
    // the right first paint, but not the thing to bisect: an observation of
    // it would seed the bracket for a different size. The re-render resolve()
    // queues brings this straight back with `fit` set.
    if (fitRef.current === null) return;

    // Never bisect against a fallback face. Before Archivo lands the browser
    // paints the metric-adjusted fallback, whose line boxes are close but
    // not the ones that count; a probe that overruns on those metrics
    // narrows the bracket on the wrong font and commits a size the real face
    // would not have chosen. Ask for the face once and let the settled
    // promise re-run this measurement; if the request fails, measure anyway,
    // because a plate frozen at the server's answer beats one that never
    // verifies at all.
    if (!fontSettledRef.current && !plateFontLoaded(el)) {
      if (!fontRequestedRef.current && typeof document !== "undefined" && document.fonts?.load) {
        fontRequestedRef.current = true;
        const settle = () => {
          fontSettledRef.current = true;
          if (mountedRef.current) setFontsNonce((n) => n + 1);
        };
        document.fonts.load(plateFontSpec(el), el.textContent ?? "").then(settle, settle);
      }
      return;
    }

    // One rect per rendered line: the browser telling us where it broke,
    // rather than us inferring it from character counts.
    const rects = el.getClientRects();
    const lines = rects.length;
    if (lines === 0) return;

    const rendered = parseFloat(getComputedStyle(el).fontSize);
    // A size that did not parse cannot bracket anything: NaN compares false
    // against every bound and would otherwise be committed as "NaNpx".
    if (!Number.isFinite(rendered) || rendered <= 0) return;
    const height = Math.round(lines * rendered * PLATE_LH);

    // With `overflow-wrap: normal` (see typeStyle below) a word that cannot
    // fit on one line never breaks — it overflows the line box instead, which
    // is exactly what makes the overrun visible to `getClientRects()`. A line
    // wider than the container is the signature of that overflow: `lines` and
    // `height` both read as fine (an overlong word still renders as "one
    // line"), so without this check a size whose only unfittable word simply
    // overflowed sideways was indistinguishable from a size that genuinely
    // fit. 1px of slop absorbs sub-pixel layout rounding.
    const usableNow = el.parentElement?.getBoundingClientRect().width ?? Infinity;
    let widestLine = 0;
    for (const rect of rects) {
      if (rect.width > widestLine) widestLine = rect.width;
    }
    const wordOverflows = widestLine > usableNow + 1;

    const report = (size: number, stepped: boolean) =>
      onMeasureRef.current?.({
        size,
        lines,
        height,
        stepped,
        isPlate: size >= PLATE_FLOOR,
      });

    const capNow = capFor(window.innerWidth);
    const budget = plateHeightBudgetFor(capNow);

    // Search rather than compute.
    //
    // Three earlier attempts tried to derive the right size in one shot and
    // each failed in its own way. Scaling by the line overrun ignored that
    // height, not lines, is the constraint. Re-deriving the advance-width
    // constant drifted, ratcheting 120 -> 93 -> 75px, because a line count is a
    // ceiling and inverting it overestimates. Calibrating on summed line widths
    // removed the drift but also the signal: the model cannot see word
    // boundaries, and real breaking wastes up to a word per line.
    //
    // So bisect on the one thing that is not a model: what the browser
    // rendered. Each pass narrows the bracket and remembers the largest size
    // measured inside the budget. Bounded passes, monotonically narrowing, and
    // the answer is always a size that was observed to fit rather than one
    // predicted to. The solver now reads the font's own table, so this
    // usually confirms on the first pass; it stays because the table is a
    // model of Chromium's line breaker and a browser is allowed to disagree.
    //
    // Both constraints, not just the budget. The height budget alone let a
    // long SEO title settle at five lines on /download: 5 x 60px x 0.86 is
    // 258px, inside the 260px budget and outside the treatment. The line
    // ceiling is what keeps a plate looking like a plate rather than a
    // paragraph set in display type. Shrinking reduces the line count as well
    // as the height, so both constraints move the same way and the bracket
    // stays monotonic.
    if (height <= budget && lines <= MAX_LINES && !wordOverflows) {
      bestRef.current = Math.max(bestRef.current ?? 0, rendered);
      loRef.current = Math.max(loRef.current, rendered);
    } else {
      hiRef.current = Math.min(hiRef.current ?? Infinity, rendered);
    }

    const lo = loRef.current;
    const hi = hiRef.current;

    // Never overran, so the solver's answer stands and there is nothing to
    // search. The common case, and it costs no extra layout.
    if (hi === null) {
      report(rendered, stepsRef.current > 0);
      return;
    }

    // Even the floor overruns: this string is not a plate at this width. The
    // fallback is a heading, so it drops the uppercase treatment.
    if (bestRef.current === null && hi <= PLATE_FLOOR) {
      const fallback = plateFallbackFor(capNow);
      if (rendered !== fallback) {
        stepsRef.current += 1;
        setFit({ size: fallback, isPlate: false });
      }
      report(fallback, true);
      return;
    }

    const next = Math.floor((lo + hi) / 2);
    const converged = hi - lo <= 2 || next <= lo || next >= hi;

    if (converged || stepsRef.current >= MAX_STEPS) {
      // Commit the largest size actually observed to fit. Falling back to the
      // floor rather than to `rendered` matters: the current render may be the
      // probe that overran, and committing it would ship the overrun.
      const settled = bestRef.current ?? PLATE_FLOOR;
      if (settled !== rendered) setFit({ size: settled, isPlate: true });
      report(settled, true);
      return;
    }

    stepsRef.current += 1;
    setFit({ size: next, isPlate: true });
    report(next, true);
  }, []);

  // Pass one on mount: replace the server's pair with the real container's
  // answer. In a layout effect, so the pair never paints after hydration.
  useIsomorphicLayoutEffect(() => {
    resolve();
  }, [resolve]);

  // Pass two, before paint: step down if the real wrap overran. `fontsNonce`
  // is here so the font-load paths genuinely re-measure: the re-solve alone
  // usually changes nothing (same arithmetic, same answer), and an unchanged
  // `fit` would otherwise leave the cleared bracket unmeasured.
  useIsomorphicLayoutEffect(() => {
    measure();
  }, [measure, fit?.size, fit?.isPlate, text, fontsNonce]);

  // Re-solve when the available width changes. Coalesced through rAF so a drag
  // does not thrash layout.
  //
  // Both a ResizeObserver and a window listener, deliberately. The observer
  // catches the container changing without the viewport changing, which a
  // window listener cannot see. The window listener covers environments where
  // ResizeObserver is unavailable or inert: it measured zero callbacks in the
  // preview browser used to verify this, including the initial observation
  // that always fires, so relying on it alone would leave the plate stuck at
  // whatever width it first hydrated at. Re-solving twice is harmless because
  // resolve() bails when the answer has not changed.
  useEffect(() => {
    const parent = spanRef.current?.parentElement;
    if (!parent) return;

    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(resolve);
    };

    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    ro?.observe(parent);
    window.addEventListener("resize", schedule, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      ro?.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [resolve]);

  // Re-solve once every webfont has loaded.
  //
  // measure() already refuses to bisect on a fallback face and asks for the
  // plate's own; this is the belt to that brace. fonts.ready resolves after
  // every pending load on the page, including faces this element never
  // requested but whose arrival can reflow the column (the descriptor, the
  // eyebrow) and change the container width. Clearing the bracket then lets
  // the search run once more against the settled page.
  //
  // resolve() alone is not enough to make that happen: it is pure arithmetic
  // over the same inputs, so it returns the same answer, setFit bails on
  // equality, and no re-render — hence no re-measure — occurs against the
  // bracket it just cleared. Bumping `fontsNonce` is what forces the measure
  // effect to run again.
  //
  // No `fonts.status === "loaded"` early-out: this effect runs after paint,
  // so the font can finish loading between the pre-paint measure and this
  // subscription, and the early-out would skip the one re-measure that
  // matters. fonts.ready resolves immediately when already loaded, and the
  // extra pass is a no-op when nothing changed.
  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts) return;

    let cancelled = false;
    document.fonts.ready.then(() => {
      if (cancelled) return;
      resolve();
      setFontsNonce((n) => n + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [resolve]);

  const Wrapper = as;

  // The two style modes. Both keep an inline `fontSize`, which the sweep and
  // the review probes use to find the wrapper.
  //
  // Server pair (fit === null): the breakpoint rule above selects the active
  // `--ls-plate-*` from the `-sm`/`-md` pairs set here. Every consumer names
  // the mobile pair as its var() fallback, so a page that somehow lost the
  // rule still renders the mobile answer, which is what the old server pass
  // shipped everywhere.
  //
  // Client fit: plain inline declarations, which outrank the rule.
  const sm = treatmentFor(solvedSm.isPlate);
  const md = treatmentFor(solvedMd.isPlate);
  const committed = treatmentFor(fit?.isPlate ?? false);

  const typeStyle: React.CSSProperties = fit
    ? {
        fontSize: `${fit.size}px`,
        fontWeight: committed.weight,
        lineHeight: committed.lineHeight,
        letterSpacing: committed.letterSpacing,
        textTransform: committed.textTransform,
        ...(fit.isPlate ? { transform: `translateX(${committed.shift})` } : null),
      }
    : ({
        "--ls-plate-sm-size": `${solvedSm.size}px`,
        "--ls-plate-sm-weight": String(sm.weight),
        "--ls-plate-sm-lh": String(sm.lineHeight),
        "--ls-plate-sm-track": sm.letterSpacing,
        "--ls-plate-sm-case": sm.textTransform,
        "--ls-plate-sm-shift": sm.shift,
        "--ls-plate-md-size": `${solvedMd.size}px`,
        "--ls-plate-md-weight": String(md.weight),
        "--ls-plate-md-lh": String(md.lineHeight),
        "--ls-plate-md-track": md.letterSpacing,
        "--ls-plate-md-case": md.textTransform,
        "--ls-plate-md-shift": md.shift,
        fontSize: "var(--ls-plate-size, var(--ls-plate-sm-size))",
        fontWeight: "var(--ls-plate-weight, var(--ls-plate-sm-weight))",
        lineHeight: "var(--ls-plate-lh, var(--ls-plate-sm-lh))",
        letterSpacing: "var(--ls-plate-track, var(--ls-plate-sm-track))",
        textTransform: "var(--ls-plate-case, var(--ls-plate-sm-case))",
        transform: "translateX(var(--ls-plate-shift, var(--ls-plate-sm-shift)))",
      } as React.CSSProperties);

  // Rule 3 only applies to a real plate. Below the floor this is a heading, and
  // a signal clipped to a 31px fallback would be noise rather than a readout.
  // Keep the overlay mounted for the whole lifetime of the plate. Mounting it
  // only while audio is active makes the generated text layer enter/leave the
  // paint tree at `ended`, which produces a perceptual nudge even though the
  // base copy's layout box is unchanged. Before the client commits, the
  // overlay is mounted if EITHER breakpoint plates: its ::after inherits the
  // active case and size, so on a breakpoint that fell back it paints the
  // same sentence-case text at 0 opacity and nothing shows.
  const showSignal = fit ? fit.isPlate : solvedSm.isPlate || solvedMd.isPlate;
  const signalActive = level !== undefined;

  return (
    <Wrapper
      // §17.9: the plate is exempt from grid-SNAPPING (it is fluid by
      // clamp/measurement, not a fixed size) but that is a different
      // question from whether adjacent text should measure glyph-edge to
      // glyph-edge — it should, so the fixed marginTop below it (the
      // descriptor in plate-heading.tsx) reads as the spacing token it
      // actually is instead of that token plus half-leading. ls-trim is a
      // block-container property (§15.6); it is safe here because Wrapper
      // is the block box, not the inline measurement span below.
      className={className ? `${className} ls-trim ls-plate-type` : "ls-trim ls-plate-type"}
      style={{
        display: "block",
        position: "relative",
        // `normal`, not `anywhere`: breaking BETWEEN words is fine, breaking
        // INSIDE one is the bug (PRODUCT.md's 20-char longest-word floor
        // exists to be respected, not routed around). `anywhere` made every
        // size in the solver's search "fit" — a too-long word just fractured
        // instead of overflowing, so the measuring layer could never see the
        // overrun. With `normal`, an unfittable word overflows its line box,
        // which is a condition `measure()` checks for explicitly.
        overflowWrap: "normal",
        wordBreak: "normal",
        ...typeStyle,
      }}
    >
      {/* Hoisted to <head> and deduped by href; one copy per document. */}
      <style href={BREAKPOINT_STYLE_HREF} precedence="default">
        {BREAKPOINT_STYLE}
      </style>

      {/* Inline, so getClientRects returns one rect per line. */}
      <span ref={spanRef} className="ls-plate-copy">
        {text}
      </span>

      {showSignal ? (
        /*
         * Rule 3. An EMPTY, aria-hidden element: the readout text comes from
         * ::after via data-text, so it never enters the h1's textContent and
         * never reaches assistive tech. See app/plate-and-hairline.css for why
         * both of those matter — a duplicated text node here put the title into
         * the h1 twice and broke the SEO guarantee the rest of this work keeps.
         *
         * The base text above is untouched and stays solid ink in every frame,
         * so the band only ever adds accent over ink and the readable title's
         * contrast never changes.
         */
        <span
          aria-hidden="true"
          className="ls-plate-signal"
          data-text={text}
          data-active={signalActive ? "true" : "false"}
          style={{ "--ls-band": `${bandTop}%` } as React.CSSProperties}
        />
      ) : null}
    </Wrapper>
  );
}
